import React, { useEffect, useState } from 'react';
import { getDeliveryLocation, getProfilePostalCode, saveDeliveryLocation } from '../../../helpers/LocationHelper';

const DeliveryLocationModal = () => {
    const [open, setOpen] = useState(false);
    const [postalCode, setPostalCode] = useState('');
    const [coordinates, setCoordinates] = useState(null);
    const [area, setArea] = useState('');
    const [error, setError] = useState('');
    const [detecting, setDetecting] = useState(false);

    useEffect(() => {
        const saved = getDeliveryLocation();
        if (!saved?.postalCode) {
            setPostalCode(getProfilePostalCode());
            setOpen(true);
        }

        const openLocationSelector = () => {
            const current = getDeliveryLocation();
            setPostalCode(current?.postalCode || getProfilePostalCode());
            setArea(current?.area || '');
            setCoordinates(current?.latitude && current?.longitude ? {
                latitude: current.latitude,
                longitude: current.longitude
            } : null);
            setError('');
            setOpen(true);
        };

        window.addEventListener('open-delivery-location', openLocationSelector);
        return () => window.removeEventListener('open-delivery-location', openLocationSelector);
    }, []);

    const detectLocation = () => {
        setError('');
        if (!navigator.geolocation) {
            setError('Location detection is not supported by this browser.');
            return;
        }

        setDetecting(true);
        navigator.geolocation.getCurrentPosition(async position => {
            const nextCoordinates = { latitude: position.coords.latitude, longitude: position.coords.longitude };
            setCoordinates(nextCoordinates);
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${nextCoordinates.latitude}&lon=${nextCoordinates.longitude}`);
                const result = await response.json();
                const detectedPostalCode = String(result?.address?.postcode || '').replace(/\D/g, '').slice(0, 6);
                if (!/^\d{6}$/.test(detectedPostalCode)) throw new Error('PIN unavailable');
                setPostalCode(detectedPostalCode);
                setArea([result.address.suburb || result.address.village || result.address.town || result.address.city, result.address.state].filter(Boolean).join(', '));
            } catch (_) {
                setError('We found your location but could not identify its PIN code. Please enter it manually.');
            } finally {
                setDetecting(false);
            }
        }, () => {
            setDetecting(false);
            setError('Location permission was unavailable. Please enter your PIN code.');
        }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    };

    const applyLocation = event => {
        event.preventDefault();
        if (!/^\d{6}$/.test(postalCode)) {
            setError('Enter a valid 6-digit PIN code.');
            return;
        }
        saveDeliveryLocation({ postalCode, area, ...coordinates, selectedAt: new Date().toISOString() });
        setOpen(false);
        window.location.reload();
    };

    if (!open) return null;

    return <div className="delivery-location" role="dialog" aria-modal="true" aria-labelledby="delivery-location-title">
        <div className="delivery-location__backdrop" />
        <div className="delivery-location__card">
            <div className="delivery-location__icon"><i className="fas fa-map-marker-alt" /></div>
            <h2 id="delivery-location-title">Find products near you</h2>
            <p>Select your delivery location to see products available in your PIN code and nearby areas.</p>
            <button className="delivery-location__detect" type="button" onClick={detectLocation} disabled={detecting}>
                <i className={`fas ${detecting ? 'fa-spinner fa-spin' : 'fa-crosshairs'}`} /> {detecting ? 'Detecting location...' : 'Use my current location'}
            </button>
            <div className="delivery-location__divider"><span>or enter PIN code</span></div>
            <form onSubmit={applyLocation}>
                <div className="delivery-location__input-row">
                    <input inputMode="numeric" maxLength="6" value={postalCode} onChange={event => setPostalCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit PIN code" aria-label="PIN code" autoFocus />
                    <button type="submit">Apply</button>
                </div>
                {area && <div className="delivery-location__area"><i className="fas fa-check-circle" /> {area}</div>}
                {error && <div className="delivery-location__error" role="alert">{error}</div>}
            </form>
            <button className="delivery-location__skip" type="button" onClick={() => setOpen(false)}>Not now</button>
            <small>Location data is used only to improve product availability and delivery estimates.</small>
        </div>
    </div>;
};

export default DeliveryLocationModal;
